// src/pages/OrdersPage.js
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OrdersList from '../components/OrdersList';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OrderEntryModal from '../components/modals/OrderEntryModal';
import { getCompanies } from '../services/api';
import { neonBlue, shakespeare } from '../theme/colors';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const buildYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2020;
  return Array.from({ length: currentYear - startYear + 1 }, (_, index) => currentYear - index);
};

const filterControlSx = {
  minWidth: 140,
  bgcolor: alpha('#fff', 0.95),
  borderRadius: 1,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
};

const OrdersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const yearOptions = buildYearOptions();
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super-admin';
  const canCreateOrder = userRole === 'clerk' || userRole === 'super-admin';

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const companyId = queryParams.get('company');
    const month = queryParams.get('month');
    const year = queryParams.get('year');

    if (companyId) {
      setSelectedCompany(companyId);
      if (companies.length > 0) {
        const company = companies.find((c) => c.id === parseInt(companyId, 10));
        setSelectedCompanyName(company ? company.name : '');
      }
    } else {
      setSelectedCompany('');
      setSelectedCompanyName('');
    }

    setSelectedMonth(month ? Number(month) : '');
    setSelectedYear(year ? Number(year) : '');
  }, [location.search, companies]);

  const updateSearchParams = (updates) => {
    const queryParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        queryParams.set(key, value);
      } else {
        queryParams.delete(key);
      }
    });

    const newSearch = queryParams.toString();
    navigate(`/orders${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      if (isSuperAdmin) {
        try {
          const response = await getCompanies();
          setCompanies(response.data || []);
        } catch (err) {
        }
      }
    };

    fetchCompanies();
  }, [isSuperAdmin]);

  const handleRefreshOrders = useCallback(() => {
    setListRefreshKey((prevKey) => prevKey + 1);
  }, []);

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompany(companyId);

    const company = companies.find((c) => c.id === parseInt(companyId, 10) || c.id === companyId);
    setSelectedCompanyName(company ? company.name : '');

    updateSearchParams({ company: companyId });
  };

  const handleMonthChange = (event) => {
    const month = event.target.value;
    setSelectedMonth(month);
    updateSearchParams({
      month: month ? String(month) : '',
      year: selectedYear ? String(selectedYear) : '',
    });
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    updateSearchParams({
      month: selectedMonth ? String(selectedMonth) : '',
      year: year ? String(year) : '',
    });
  };

  const pageTitle = selectedCompanyName ? `${selectedCompanyName} Orders` : 'Manage Orders';

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          background: `linear-gradient(135deg, ${neonBlue[600]} 0%, ${neonBlue[500]} 50%, ${shakespeare[500]} 100%)`,
          color: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {pageTitle}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: alpha('#fff', 0.85) }}>
              Track, search, and manage delivery orders
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
            <FormControl size="small" sx={filterControlSx}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={handleMonthChange}
                label="Month"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={filterControlSx}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                label="Year"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {yearOptions.map((yearOption) => (
                  <MenuItem key={yearOption} value={yearOption}>
                    {yearOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isSuperAdmin && (
              <FormControl size="small" sx={{ ...filterControlSx, minWidth: 220 }}>
                <InputLabel>Filter by Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Filter by Company"
                >
                  <MenuItem value="">
                    <em>All Companies</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {canCreateOrder && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsOrderModalOpen(true)}
                sx={{
                  bgcolor: '#fff',
                  color: neonBlue[600],
                  fontWeight: 600,
                  '&:hover': { bgcolor: alpha('#fff', 0.9) },
                }}
              >
                New Order
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <OrdersList key={listRefreshKey} onActionCompletes={handleRefreshOrders} />

      <OrderEntryModal
        open={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        onOrderCreated={handleRefreshOrders}
      />
    </Box>
  );
};

export default OrdersPage;
